import { X } from "lucide-react";
import {
  Text,
  InputField,
  IconButton,
  Checkbox,
  Button,
} from "@components/atoms";

interface AccountCreationStepProps {
  fullName: string;
  cedula: string;
  phone: string;
  address: string;
  acceptTerms: boolean;
  acceptNotifications: boolean;
  onFullNameChange: (name: string) => void;
  onCedulaChange: (cedula: string) => void;
  onPhoneChange: (phone: string) => void;
  onAddressChange: (address: string) => void;
  onFullNameBlur: () => void;
  onCedulaBlur: () => void;
  onPhoneBlur: () => void;
  onAddressBlur: () => void;
  onAcceptTermsChange: (accepted: boolean) => void;
  onAcceptNotificationsChange: (accepted: boolean) => void;
  onClose: () => void;
  isValid: boolean;
  isLoading?: boolean;
  onContinue: () => void;
  fullNameError?: string;
  cedulaError?: string;
  phoneError?: string;
  addressError?: string;
}

/**
 * Account creation step component for LoginModal
 */
export function AccountCreationStep({
  fullName,
  cedula,
  phone,
  address,
  acceptTerms,
  acceptNotifications,
  onFullNameChange,
  onCedulaChange,
  onPhoneChange,
  onAddressChange,
  onFullNameBlur,
  onCedulaBlur,
  onPhoneBlur,
  onAddressBlur,
  onAcceptTermsChange,
  onAcceptNotificationsChange,
  onClose,
  isValid,
  isLoading,
  onContinue,
  fullNameError,
  cedulaError,
  phoneError,
  addressError,
}: AccountCreationStepProps) {
  return (
    <div className="px-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Text
          variant="h2"
          as="h2"
          className="font-semibold text-black"
          style={{ fontSize: 20 }}
        >
          Creación de cuenta
        </Text>
        <IconButton size={24} onClick={onClose}>
          <X size={24} className="text-black" />
        </IconButton>
      </div>

      <Text variant="body" as="p" className="mt-2 text-black">
        Solo necesitamos un poco más de información para crear tu cuenta.
      </Text>

      {/* Two-column form */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        <div>
          <InputField
            label="Nombres y Apellidos"
            placeholder="Juan Pérez"
            value={fullName}
            onChange={(e) => onFullNameChange(e.target.value)}
            onBlur={onFullNameBlur}
            className="w-full"
          />
          {fullNameError && (
            <Text variant="body" as="p" className="mt-1 text-red-500 text-sm">
              {fullNameError}
            </Text>
          )}
        </div>

        <div>
          <InputField
            label="Cédula"
            placeholder="0912345678"
            value={cedula}
            onChange={(e) => onCedulaChange(e.target.value)}
            onBlur={onCedulaBlur}
            className="w-full"
          />
          {cedulaError && (
            <Text variant="body" as="p" className="mt-1 text-red-500 text-sm">
              {cedulaError}
            </Text>
          )}
        </div>

        <div>
          <InputField
            label="Celular"
            placeholder="0999999999"
            value={phone}
            onChange={(e) => onPhoneChange(e.target.value)}
            onBlur={onPhoneBlur}
            className="w-full"
          />
          {phoneError && (
            <Text variant="body" as="p" className="mt-1 text-red-500 text-sm">
              {phoneError}
            </Text>
          )}
        </div>

        <div>
          <InputField
            label="Dirección"
            placeholder="Guayaquil, Ecuador"
            value={address}
            onChange={(e) => onAddressChange(e.target.value)}
            onBlur={onAddressBlur}
            className="w-full"
          />
          {addressError && (
            <Text variant="body" as="p" className="mt-1 text-red-500 text-sm">
              {addressError}
            </Text>
          )}
        </div>
      </div>

      {/* Checkboxes */}
      <div className="mt-5 flex flex-col gap-4 px-8 pt-4">
        <Checkbox
          checked={acceptTerms}
          onChange={onAcceptTermsChange}
          label={
            <>
              Acepto los Términos y Condiciones y la{" "}
              <span className="text-neutral-40 underline">
                Política de Privacidad
              </span>
              . Autorizo el tratamiento de mi número de cédula, datos
              biométricos y datos de contacto para la validación de mi
              identidad, seguridad en los accesos y el envío de mis tickets
              virtuales vía correo electrónico y WhatsApp.
            </>
          }
        />
        <Checkbox
          checked={acceptNotifications}
          onChange={onAcceptNotificationsChange}
          label="Me gustaría recibir notificaciones sobre preventas exclusivas, anuncios de nuevos eventos y promociones personalizadas."
        />
      </div>

      {/* Continue button */}
      <Button
        variant="primary"
        className="mt-5 mx-auto h-[50px] w-full max-w-[350px] bg-primary-110 text-white hover:bg-primary-110 disabled:bg-neutral-20 disabled:text-neutral-70 disabled:opacity-100"
        disabled={!isValid}
        onClick={onContinue}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-4 w-4 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12" cy="12" r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
            Creando cuenta...
          </span>
        ) : (
          "Continuar"
        )}
      </Button>
    </div>
  );
}
